import { z } from 'zod'

const currentYear = new Date().getFullYear()

export const INQUIRY_CONTACT_METHODS = ['email', 'sms', 'call'] as const
export type InquiryContactMethod = (typeof INQUIRY_CONTACT_METHODS)[number]

/**
 * The four vehicle photos the /quote form asks for, in display order. The
 * estimator wants the two full sides and the rear straight on, plus one shot
 * from a front corner looking down so the hood and roof (the two largest
 * panels) are both in frame. Keys travel on the FormData as `photo:<key>`.
 */
export const QUOTE_PHOTO_ANGLES = [
  { key: 'driver', label: 'Driver side', hint: 'Straight on, whole car in frame' },
  { key: 'passenger', label: 'Passenger side', hint: 'Straight on, whole car in frame' },
  { key: 'rear', label: 'Rear', hint: 'Straight on from behind' },
  { key: 'front-corner', label: 'Front corner', hint: 'From a front corner, looking down at hood and roof' },
] as const
export type QuotePhotoAngle = (typeof QUOTE_PHOTO_ANGLES)[number]['key']

export const QUOTE_PHOTO_LIMITS = {
  maxBytes: 12 * 1024 * 1024,
  acceptedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'] as const,
} as const

/**
 * Shared input for the /contact general form and the /quote smart lead form.
 * `inquiryType` is the discriminator persisted on ContactInquiry; `source`
 * names the surface that posted it so leads can be attributed per entry point.
 */
export const contactInquirySchema = z.object({
  inquiryType: z.enum(['GENERAL', 'QUOTE']).default('GENERAL'),
  source: z.string().max(40).optional().or(z.literal('')),
  name: z.string().trim().min(1, 'Name is required').max(200),
  email: z.string().trim().email('Valid email is required'),
  phone: z.string().trim().max(40).optional().or(z.literal('')),
  contactMethod: z.enum(INQUIRY_CONTACT_METHODS).default('email'),
  companyName: z.string().trim().max(120).optional().or(z.literal('')),
  vehicleYear: z.coerce
    .number()
    .int()
    .min(1900)
    .max(currentYear + 2)
    .optional()
    .transform((v) => (v && v > 0 ? v : undefined)),
  vehicleMake: z.string().trim().max(50).optional().or(z.literal('')),
  vehicleModel: z.string().trim().max(128).optional().or(z.literal('')),
  vin: z
    .string()
    .trim()
    .regex(/^[A-HJ-NPR-Z0-9]{17}$/i, 'VIN must be 17 characters (letters and numbers, no I, O, or Q)')
    .optional()
    .or(z.literal('')),
  serviceInterest: z.string().trim().max(200).optional().or(z.literal('')),
  timeline: z.string().trim().max(60).optional().or(z.literal('')),
  message: z.string().trim().max(5000).optional().or(z.literal('')),
  shopLocationId: z.string().cuid(),
  // Honeypot: real users never see or fill this field. Any value = bot. It is
  // deliberately not length-capped: a `max(0)` rule would fail validation and
  // hand the bot an error, and the action's pretend-success branch (the point
  // of a honeypot) would never be reached.
  website: z.string().optional(),
  // Cloudflare Turnstile token from the widget. Optional in the schema so an
  // unconfigured environment still parses; the action decides whether a
  // missing token blocks the submission.
  turnstileToken: z.string().optional(),
})

export type ContactInquiryInput = z.input<typeof contactInquirySchema>
export type ContactInquiryData = z.output<typeof contactInquirySchema>
